import { originalUrl as originalImageUrl, sizedUrl as sizedImageUrl } from 'hasResizableImages';
import angularModule from '../../../../../../lessons_module';

/*
    an Image is a UiComponent that will be rendered as an <img> tag when included on the screen.

    It should include the to-jsonned version of an S3Asset, which will allow it
    to determine the src of the image.

    Example:

    {
	    "id": "49cfa0e4-18a1-41b1-8c1a-d30f44c7b44b",
	    "__type": "Lesson::FrameList::Frame::Componentized::Component::Image",
	    "component_type": "ComponentizedFrame.ImageModel",
        "image": {
            "id": 1,
            "file_file_name": "three_cluster_2_3_6.png",
            "formats": {
                "original": {
                    "url": "https://s3.amazonaws.com/pedago/front_royal_assets/lessons/bf9e8941-d9a3-4593-b32b-2730071bef39/images/Fire1/original/Fire1.png?1390925319"
                }
            }
        }
    }
*/
angularModule.factory('Lesson.FrameList.Frame.Componentized.Component.Image.ImageModel', [
    'Lesson.FrameList.Frame.Componentized.Component.UiComponent.UiComponentModel',
    'Lesson.FrameList.Frame.Componentized.Component.Image.ImageViewModel',
    '$injector',
    (UiComponentModel, ImageViewModel, $injector) => {
        const $window = $injector.get('$window');

        const ImageModel = UiComponentModel.subclass(function subclass() {
            this.alias('ComponentizedFrame.Image');
            this.extend({
                ViewModel: ImageViewModel,
            });
            this.setEditorViewModel('Lesson.FrameList.Frame.Componentized.Component.Image.ImageEditorViewModel');

            Object.defineProperty(this.prototype, 'hasImage', {
                get() {
                    return !!this.image || !!this.$$dataUrl;
                },
            });

            Object.defineProperty(this.prototype, 'aspectRatio', {
                get() {
                    let aspectRatio;

                    // wrap in try/catch in case any of these are missing
                    try {
                        aspectRatio = this.image.dimensions.width / this.image.dimensions.height;
                        // oxlint-disable-next-line no-empty
                    } catch {}

                    // do not return NaN, return undefined
                    return aspectRatio || undefined;
                },
                configurable: true,
            });

            Object.defineProperty(this.prototype, 'src', {
                get() {
                    return originalImageUrl(this.image) || this.$$dataUrl;
                },
                set(val) {
                    this.image = {
                        src: val,
                        formats: {
                            original: {
                                url: val,
                            },
                        },
                    };
                },
            });

            Object.defineProperty(this.prototype, 'uploadingImage', {
                get() {
                    return !this.image && this.$$dataUrl;
                },
            });

            Object.defineProperty(this.prototype, 'allowUnreferenced', {
                get() {
                    // if no image has been uploaded and the image component
                    // is unreferenced, then let it be cleaned up on save
                    return !!this.image;
                },
            });

            this.key('image');
            this.key('label');
            return {
                originalUrl() {
                    // if image is not set, try to fallback to the dataUrl, which
                    // will be set in the editor if this file is still being uploaded
                    if (this.uploadingImage) {
                        return this.$$dataUrl;
                    }

                    const url = originalImageUrl(this.image);
                    if (!url) {
                        return null;
                    }

                    return this.frame().getAlteredUrl(url);
                },

                sizedUrl(width, height) {
                    // if image is not set, try to fallback to the dataUrl, which
                    // will be set in the editor if this file is still being uploaded
                    if (this.uploadingImage) {
                        return this.$$dataUrl;
                    }

                    const url = sizedImageUrl(this.image, width, height);
                    if (!url) {
                        return null;
                    }

                    return this.frame().getAlteredUrl(url);
                },

                clone($super, newId) {
                    const model = $super(newId);
                    model.$$dataUrl = this.$$dataUrl;
                    return model;
                },

                urlForContext(context, forceMaxPixelRatio) {
                    // if image is not set, try to fallback to the dataUrl, which
                    // will be set in the editor if this file is still being uploaded
                    if (!this.image && this.$$dataUrl) {
                        return this.$$dataUrl;
                    }
                    // isMobile and devicePixelRatio are added to the lesson in FrontRoyalStoreApi#getPublishedStream.
                    // If they are set, it means we are loading a lesson image that was saved in the store.
                    // Having these values from the time that they were stored allows us to correctly load them when offline
                    // even if the device window changed size.
                    const { is_mobile: isMobile, device_pixel_ratio: devicePixelRatio } = this.lesson;

                    const dimensions = this._dimensionsForContext({
                        context,
                        forceMaxPixelRatio,
                        isMobile,
                        devicePixelRatio,
                    });

                    return dimensions ? this.sizedUrl(dimensions[0], dimensions[1]) : this.originalUrl();
                },

                replaceDataUrlWithImage(image) {
                    const dataUrl = this.$$dataUrl;

                    if (!dataUrl) {
                        // If the same image content was uploaded by two different instances of content-item-image-upload
                        // at once, then this model will already have had it's dataUrl replaced with the
                        // real image content.  If that's the case, though, then everything is fine and we can just go on.
                        return;
                    }

                    // replace the image on any image that has this dataUrl (important
                    // if the image has been cloned when switching frame types or copying
                    // image across frames)
                    // image across frames)
                    if (this.frame()) {
                        this.frame()
                            .lesson()
                            .frames.forEach(frame => {
                                frame.componentsForType(ImageModel).forEach(imageModel => {
                                    if (imageModel.$$dataUrl === dataUrl) {
                                        imageModel.image = angular.copy(image);
                                        imageModel.$$dataUrl = undefined;
                                    }
                                });
                            });
                    }
                },

                /*
                    CSS-dictated image sizing (standard res / retina)

                        content:
                            < 768px:        510x200 / 1020x400
                            >= 768px:       700x275 / 1400x550

                        inline:
                            *:              84x33 / 168x66

                        answer buttons:
                            < 768px:        293x115 / 586x230
                            >= 768px:       408x160 / 816x320

                        tile prompts:
                            < 768px:        344x135 / 688x270
                            >= 768px:       573x225 / 1146x450

                    */
                // oxlint-disable-next-line complexity
                _dimensionsForContext({ context, forceMaxPixelRatio, isMobile, devicePixelRatio }) {
                    const useMobileImage = this._checkIsMobile(isMobile);
                    const pixelRatio = this._getDevicePixelRatio(devicePixelRatio);
                    let baseDimensions;

                    context = context || 'original';
                    // The image-resizer worker passes GIFs through un-resized, and resizing
                    // animated gifs has historically produced artifacts (transparency, etc.),
                    // so always use the original version when it's a gif.
                    context = this._isGIF() ? 'original' : context;

                    // determine the most appropriate resolution for given context
                    switch (context) {
                        case 'original':
                        case 'zoom':
                            return null;

                        case 'tallContextImage':
                        case 'shortContextImage':
                        case 'secondImage':
                        case 'challengeOverlayBlank':
                        case 'wideAnswerButton':
                        case 'display':
                            if (useMobileImage) {
                                baseDimensions = [510, 200];
                            } else {
                                baseDimensions = [700, 275];
                            }
                            break;

                        case 'answerButton':
                            if (useMobileImage) {
                                baseDimensions = [293, 115];
                            } else {
                                baseDimensions = [408, 160];
                            }
                            break;

                        case 'tilePrompt':
                            if (useMobileImage) {
                                baseDimensions = [344, 135];
                            } else {
                                baseDimensions = [573, 225];
                            }
                            break;

                        case 'inline':
                            baseDimensions = [84, 33];
                            break;

                        default:
                            throw new Error(`Unable to find format for unknown context "${context}"`);
                    }

                    let x = 1;

                    if (pixelRatio > 2 || forceMaxPixelRatio) {
                        x = 3;
                    } else if (pixelRatio > 1) {
                        x = 2;
                    }

                    return baseDimensions.map(n => x * n);
                },

                // separate method to stub in tests
                _checkIsMobile(isMobile) {
                    // Use width OR height because smaller tablets can change orientation, and we need to
                    // be able to handle that and show offline images.
                    return isMobile ?? ($window.innerWidth < 768 || $window.innerHeight < 768);
                },

                // separate method to stub in tests
                _getDevicePixelRatio(ratio) {
                    return ratio ?? $window.devicePixelRatio;
                },

                _isGIF() {
                    return this.src && this.src.match(/\.gif$/i);
                },
            };
        });

        return ImageModel;
    },
]);
